// src/pages/events/EventGalleryDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { uploadToGarage, useGarageUrls } from '../../../garage';

const EventGalleryDialog = ({ open, onClose, eventId, eventTitle, currentGallery = [] }) => {
  const [gallery, setGallery] = useState(currentGallery);

  useEffect(() => {
    setGallery(currentGallery);
  }, [currentGallery]);

  const galleryUrls = useGarageUrls(gallery);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [saving, setSaving] = useState(false);

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);

    try {
      const uploadPromises = files.map(async (file, index) => {
        const url = await uploadToGarage(file, 'events/gallery');
        setUploadProgress(prev => ({ ...prev, [index]: 100 }));
        return url;
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      setGallery(prev => [...prev, ...uploadedUrls]);
      setUploadProgress({});
    } catch (error) {
      console.error('Error uploading images:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (imageUrl) => {
    setGallery(prev => prev.filter(url => url !== imageUrl));
  };

  const handleSaveGallery = async () => {
    setSaving(true);
    try {
      // Actualizar el evento en Firestore con la nueva galería
      const eventRef = doc(db, 'events', eventId);
      await updateDoc(eventRef, {
        galeria: gallery,
        updatedAt: new Date()
      });

      onClose();
    } catch (error) {
      console.error('Error saving gallery:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Galería de Fotos - {eventTitle}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Añade fotos del evento para que los usuarios puedan verlas en la app móvil. 
          Las fotos aparecerán en la galería solo para eventos finalizados.
        </Typography>

        {/* Botón de subir imágenes */}
        <Box sx={{ my: 3 }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<AddIcon />}
            disabled={uploading}
            fullWidth
            sx={{ p: 2, borderStyle: 'dashed' }}
          >
            <Box textAlign="center">
              <CloudUploadIcon sx={{ fontSize: 48, mb: 1, color: 'text.secondary' }} />
              <Typography variant="h6">
                Seleccionar fotos
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Puedes seleccionar múltiples archivos de imagen
              </Typography>
            </Box>
            <input
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={handleFileUpload}
            />
          </Button>
        </Box>

        {/* Progreso de subida */}
        {uploading && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Subiendo imágenes... Por favor espera.
            </Alert>
            {Object.entries(uploadProgress).map(([index, progress]) => (
              <Box key={index} sx={{ mb: 1 }}>
                <Typography variant="caption">
                  Imagen {parseInt(index) + 1}: {Math.round(progress)}%
                </Typography>
                <Box sx={{ width: '100%', bgcolor: 'grey.300', borderRadius: 1, mt: 0.5 }}>
                  <Box
                    sx={{
                      width: `${progress}%`,
                      height: 4,
                      bgcolor: 'primary.main',
                      borderRadius: 1,
                      transition: 'width 0.3s ease'
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Galería de imágenes */}
        {gallery.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="h6" color="text.secondary">
              No hay fotos en la galería
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Añade algunas fotos del evento para que los usuarios puedan verlas.
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              {gallery.length} foto{gallery.length !== 1 ? 's' : ''} en la galería
            </Typography>
            <ImageList variant="masonry" cols={3} gap={8}>
              {gallery.map((imageUrl, index) => (
                <ImageListItem key={index}>
                  <img
                    src={galleryUrls[index] || ''}
                    alt={`Galería ${index + 1}`}
                    loading="lazy"
                    style={{
                      borderRadius: 8,
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'scale(1.02)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)';
                    }}
                    onClick={() => window.open(galleryUrls[index] || imageUrl, '_blank')}
                  />
                  <ImageListItemBar
                    actionIcon={
                      <IconButton
                        sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                        onClick={() => handleRemoveImage(imageUrl)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                    sx={{
                      background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)'
                    }}
                  />
                </ImageListItem>
              ))}
            </ImageList>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button 
          onClick={handleSaveGallery} 
          variant="contained" 
          disabled={saving || uploading}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? 'Guardando...' : 'Guardar Galería'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventGalleryDialog;